import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { Stamp } from "lucide-react";

const FALLBACK_RATES = { USD: 5.1, EUR: 5.84, GBP: 6.85, CAD: 3.62 };
const SYMBOLS = { USD: "US$", EUR: "€", GBP: "£", CAD: "CA$" };
const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "OTHER"];
const BASE_BRL = 650;

export default function Pricing() {
  const [currency, setCurrency] = useState("USD");
  const [manualRate, setManualRate] = useState("");
  const [stamped, setStamped] = useState(null);
  const [error, setError] = useState(false);
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [ratesDate, setRatesDate] = useState(null);

  useEffect(() => {
    fetch("https://api.frankfurter.dev/v1/latest?from=BRL&to=USD,EUR,GBP,CAD")
      .then((r) => r.json())
      .then((data) => {
        if (!data.rates) return;
        const live = {};
        for (const c of ["USD", "EUR", "GBP", "CAD"]) {
          if (data.rates[c]) live[c] = 1 / data.rates[c];
        }
        if (Object.keys(live).length === 4) {
          setRates(live);
          setRatesDate(data.date);
        }
      })
      .catch(() => {});
  }, []);

  const fmt = (v) => v.toFixed(2).replace(".", ",");

  const handleStamp = () => {
    let amount;
    if (currency === "OTHER") {
      const rate = parseFloat(manualRate);
      if (!rate || rate <= 0) {
        setError(true);
        return;
      }
      setError(false);
      amount = fmt(BASE_BRL / rate);
    } else {
      amount = `${SYMBOLS[currency]} ${fmt(BASE_BRL / rates[currency])}`;
    }
    setStamped({ amount, key: Date.now() });
  };

  return (
    <section id="investimento" className="py-24 md:py-32 border-t border-ink/10" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <Eyebrow>Investimento</Eyebrow>
          <h2 className="font-serif font-semibold text-4xl md:text-5xl tracking-tight text-ink mt-4">
            Um valor fixo, na sua moeda.
          </h2>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-px bg-ink/10 border border-ink/10">
          <Reveal className="bg-paper">
            <div className="p-9 md:p-14">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/55">
                Assessoria completa — valor único
              </span>
              <div className="mt-4 flex items-end gap-3 flex-wrap">
                <span className="font-serif font-semibold text-7xl md:text-8xl tracking-tight text-ink leading-none" data-testid="price-brl">
                  R$&nbsp;650
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-brass font-bold pb-2">
                  / processo inteiro
                </span>
              </div>
              <p className="text-ink/65 text-[15px] leading-relaxed mt-7 max-w-md">
                Escolha sua moeda para ver o equivalente. Sem mensalidades, sem
                surpresas: o valor corresponde à assessoria completa, do
                diagnóstico à certidão.
              </p>

              <div className="mt-9">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/55 block mb-3">
                  Sua moeda
                </span>
                <div className="flex flex-wrap gap-2" data-testid="currency-select">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c}
                      data-testid={`currency-option-${c.toLowerCase()}`}
                      onClick={() => setCurrency(c)}
                      className={`rounded-full border px-5 py-2.5 font-mono text-[12px] font-bold tracking-[0.06em] transition-colors duration-300 ${
                        currency === c
                          ? "bg-ink text-paper border-ink"
                          : "border-ink/20 text-ink/60 hover:border-ink/50 hover:text-ink"
                      }`}
                    >
                      {c === "OTHER" ? "Outra" : c}
                    </button>
                  ))}
                </div>

                <AnimatePresence>
                  {currency === "OTHER" && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.35 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-5">
                        <label htmlFor="manualRate" className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/55 block mb-2">
                          Quantos reais vale 1 unidade da sua moeda?
                        </label>
                        <input
                          id="manualRate"
                          data-testid="manual-rate-input"
                          type="number"
                          step="0.0001"
                          min="0.0001"
                          value={manualRate}
                          onChange={(e) => setManualRate(e.target.value)}
                          placeholder="Ex.: 5.10"
                          className={`w-full max-w-[240px] bg-white border ${
                            error ? "border-carimbo" : "border-ink/20"
                          } px-4 py-3 text-[15px] font-sans text-ink outline-none focus:border-brass transition-colors duration-300`}
                        />
                        {error && (
                          <p className="font-mono text-[11px] text-carimbo mt-2" data-testid="manual-rate-error">
                            Informe uma taxa válida maior que zero.
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  data-testid="stamp-button"
                  onClick={handleStamp}
                  className="group mt-7 inline-flex items-center gap-2.5 rounded-full bg-carimbo px-7 py-3.5 font-mono text-[12px] font-bold uppercase tracking-[0.1em] text-paper transition-all duration-300 hover:bg-ink hover:-translate-y-0.5"
                >
                  <Stamp className="w-4 h-4 transition-transform duration-300 group-hover:-rotate-12" />
                  Carimbar valor
                </button>

                <div className="mt-6 max-w-md">
                  <span className="inline-flex items-center gap-2" data-testid="rates-status">
                    <span className={`w-1.5 h-1.5 rounded-full ${ratesDate ? "bg-wa animate-pulse" : "bg-brass"}`} />
                    <span className={`font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${ratesDate ? "text-wa" : "text-ink/55"}`}>
                      {ratesDate ? "Cotação ao vivo · BCE" : "Cotações de referência"}
                    </span>
                  </span>
                  <p className="font-mono text-[10.5px] leading-relaxed text-ink/55 mt-2" data-testid="rates-note">
                    {ratesDate
                      ? `Atualizadas em ${new Date(ratesDate + "T12:00:00").toLocaleDateString("pt-BR")}: USD R$${fmt(rates.USD)} · EUR R$${fmt(rates.EUR)} · GBP R$${fmt(rates.GBP)} · CAD R$${fmt(rates.CAD)}.`
                      : "USD R$5,10 · EUR R$5,84 · GBP R$6,85 · CAD R$3,62."}{" "}
                    Sujeitas a variação cambial — valor corresponde à assessoria, não a taxas da Justiça Eleitoral.
                  </p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="bg-white">
            <div className="relative h-full min-h-[420px] flex items-center justify-center p-10 overflow-hidden">
              <span className="pointer-events-none select-none absolute inset-0 flex items-center justify-center font-mono text-[11px] tracking-[0.3em] text-ink/10 uppercase rotate-[-8deg]">
                Valor · Valor · Valor · Valor · Valor
              </span>
              <AnimatePresence mode="wait">
                {stamped ? (
                  <motion.div
                    key={stamped.key}
                    initial={{ scale: 3, opacity: 0, rotate: -18 }}
                    animate={{ scale: 1, opacity: 1, rotate: -7 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 200, damping: 13 }}
                    className="stamp-ring relative w-[240px] h-[240px] rounded-full bg-paper/30 flex flex-col items-center justify-center text-carimbo text-center"
                    data-testid="stamp-result"
                  >
                    <span className="font-mono font-bold text-[26px] tracking-tight" data-testid="stamp-amount">
                      {stamped.amount}
                    </span>
                    <span className="font-mono text-[10.5px] mt-2 opacity-75">≈ R$ 650,00</span>
                    <span className="font-mono text-[9px] tracking-[0.18em] uppercase mt-2.5 opacity-65">
                      Valor da assessoria
                    </span>
                  </motion.div>
                ) : (
                  <motion.p
                    key="placeholder"
                    exit={{ opacity: 0 }}
                    className="font-mono text-[12px] text-ink/55 text-center max-w-[200px] leading-relaxed"
                    data-testid="stamp-placeholder"
                  >
                    Selecione a moeda e clique em “Carimbar valor”
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
